import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {mapStatePropertiesMixin} from '@aofl/map-state-properties-mixin';
import {i18nMixin} from '@aofl/i18n-mixin';
import translations from '../../i18n';
import {storeInstance} from '@aofl/store/dist/legacy';
import {
  sdoNs,
  jobTypes,
  jobStatuses,
  pages,
  modals,
  rosterJobStages,
  licenseAssignMode, environment,
} from '@modules/constants-enumerate';
import {ApiService} from '@modules/api-service';
import {userJobs} from '@modules/user-jobs';
import {SimpleModal} from '@modules/simple-modal';
import '../../../../modules/license-assign/sdo';
import {RosterUtils} from '@modules/roster-utils';

/**
 * @summary RosterValidatePage
 * @extends {mapStatePropertiesMixin(i18nMixin(AoflElement))}
 */
@customElement('roster-validate')
class RosterValidate extends mapStatePropertiesMixin(i18nMixin(AoflElement)) {
  /**
   * Creates an instance of RosterValidate.
   */
  constructor() {
    super();

    this.storeInstance = storeInstance;
    this.translations = translations;
  }

  /**
   * @readonly
   */
  static is = 'roster-validate';

  /**
   * @type {Array}
   */
  @property({type: Array, attribute: false, mapState: `${sdoNs.SESSION}.$myGroups`, store: storeInstance})
  myGroups = [];

  @property({type: Array, attribute: false, mapState: `${sdoNs.SESSION}.group_info.level`, store: storeInstance})
  myLevel = '0';

  @property({type: Number, mapState: `${sdoNs.SESSION}.group_info.id`, store: storeInstance})
  myGroupId = 0;

  @property({type: Array, attribute: false, mapState: `${sdoNs.SESSION}.settings.tier_structure.labels`, store: storeInstance})
  tierStructureLabels = [];

  @property({type: Number, mapState: `${sdoNs.SESSION}.current_profile.user_id`, store: storeInstance})
  user_id = 0;

  @property({
    type: Object, attribute: false, async hasChanged(oldVal, newVal) {
      if (typeof oldVal.stage !== 'undefined' &&
        typeof oldVal.status !== 'undefined' &&
        typeof newVal.stage !== 'undefined' &&
        typeof newVal.status !== 'undefined' &&
        newVal.status !== jobStatuses.DONE && (
          oldVal.stage !== newVal.stage ||
          oldVal.status !== newVal.status
        )) {
        const rosterJobs = await ApiService.rosterJobGetList();
        storeInstance.commit({namespace: sdoNs.USER_JOBS, mutationId: 'setRosterJobs', payload: rosterJobs.jobs});

        if (oldVal.status === jobStatuses.FAILED && oldVal.stage === rosterJobStages.ROSTER_VALIDATE_ERRORS) {
          window.Appcues && window.Appcues.track('rosterValidateFailed');
        } else if (oldVal.status === jobStatuses.DONE
          && (newVal.status === jobStatuses.ACTIVE && newVal.stage === rosterJobStages.ROSTER_PROCESSING_COMMIT)) {
          window.Appcues && window.Appcues.track('rosterCommitDone');
        }
      }
      return true;
    }
  })
  rosterJob = {};

  @property({type: String})
  rosterUploadUrl = '';

  @property({type: String})
  replaceRosterCompareString = ''; // gets set to this.__('<tt-nuwms1cu>', 'Replace Entire Roster') in connectedCallback

  @property({type: String})
  replaceRosterConfirmation = '';

  @property({type: Boolean})
  replaceRosterConfirmationMatches = true;

  @property({type: Boolean})
  isSameGroupId = false;

  updateReplaceRosterConfirmation(e) {
    this.replaceRosterConfirmation = e.target.value.trim();
  }

  async connectedCallback() {
    super.connectedCallback();
    this.replaceRosterCompareString = await this.__('<tt-nuwms1cu>', 'Replace Entire Roster');
    this.replaceRosterCompareString = this.replaceRosterCompareString.strings[0];
    this.isSameGroupId = parseInt(this.rosterJob.group_id, 10) === this.myGroupId;
  }

  async commitRoster() {
    if (this.rosterJob.job_type !== jobTypes.ROSTER_FULL
      || (this.rosterJob.job_type === jobTypes.ROSTER_FULL
        && this.replaceRosterConfirmation === this.replaceRosterCompareString)
      // to allow for faster testing disable check on local and QAT
      || (this.rosterJob.job_type === jobTypes.ROSTER_FULL
        && environment.PROD === false)) {
      try {
        this.replaceRosterConfirmationMatches = true;
        await ApiService.rosterJobCommit(this.rosterJob.id);
        await Promise.all([userJobs.getJobs(), userJobs.getRosterJobs()]);
        this.interval = setInterval(() => userJobs.getRosterJobs(), userJobs.getDelay());
      } catch (e) {
      }
    } else if (this.rosterJob.job_type === jobTypes.ROSTER_FULL
      && this.replaceRosterConfirmation !== this.replaceRosterCompareString) {
      this.replaceRosterConfirmationMatches = false;
    }
  }

  async commitRosterNoEmailChange() {
    try {
      await ApiService.rosterJobCommit(this.rosterJob.id, {skip_emails: true});
      await userJobs.getJobs();
    } catch (e) {
    }
  }

  async cancelJob(e) {
    try {
      SimpleModal.show(modals.CANCEL_ROSTER_JOB_CONFIRMATION_MODAL, {
        rosterJobId: this.rosterJob.id,
      }, e);
    } catch (e) {
    }
  }

  async download() {
    await RosterUtils.download(this.rosterJob.id);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.interval);
  }

  openLicenseModal(e) {
    this.storeInstance.commit({
      namespace: sdoNs.LICENSE_ASSIGN,
      mutationId: 'setData',
      payload: {
        assignMode: licenseAssignMode.SELECTED,
        rosterId: this.rosterJob.id,
        subsetUrl: pages.STUDENT_SEARCH + `?filters.roster_job_id=${this.rosterJob.id}&wizard=true`
      }
    });

    SimpleModal.show(modals.LICENSE_ASSIGN_BULK, {}, e);
  }

  mapStateProperties() {
    const state = storeInstance.getState();
    if (!state[sdoNs.SESSION].loggedIn) return;
    this.rosterJob = state[sdoNs.USER_JOBS].$activeRosterJob;
    this.rosterUploadUrl = state[sdoNs.USER_JOBS].$rosterUploadUrl;
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render({
      default: {template, styles: [styles]}
    });
  }
}

export default RosterValidate;
